import React from 'react'

import { Statistic, Typography } from 'antd'
import { round } from 'lodash'
import moment from 'moment'
import styled from 'styled-components'

import { CorePageMessage } from '@/components/layout-components/CorePageMessage'
import { Tag } from '@/components/shared-components/BUI/Tag'
import { useProject } from '@/context/projectContext'
import { convertEnumToString } from '@/utils/index'

import { BudgetStatus } from 'types/api.graphql'

const Statistics = styled.div`
  display: flex;
  margin-top: 1rem;
  gap: 1.5rem;
  flex-wrap: wrap;
`

const StatWrapper = styled.div`
  border-right: 1px solid ${(props) => props.theme.colour.neutral.surface};
  margin-right: 0.5rem;
  padding-right: 0.5rem;
  height: 100%;

  @media (max-width: 767px) {
    margin-bottom: 2rem;
  }
`

type ProjectBudgetStatisticsProps = {
  showTotalHours: boolean
  budget: number
  projectCompletion?: number
  data: {
    lastLoggedCompletionPercentage?: number | null
    completionLastLoggedAt?: string | null
    status: BudgetStatus
    totalHours: number
    projectHoursByDate?: Array<{
      date: string
      projectHours: number
    }>
    projectCompletionByDate?: Array<{
      date: string
      completionPercentage: number
    }>
    subcontractors?: Array<{
      id: string
      name: string
      numberOfWorkers: number
      projectHours: number
      projectHoursByDate?: Array<{
        date: string
        projectHours: number
      }>
      workers?: Array<{
        id?: string
        name: string
        projectHours: number
      }> | null
    }> | null
  } | null
  loading: boolean
  filter?: {
    afterDateTime?: string
    beforeDateTime?: string
  }
}

const ProjectBudgetStatistics = ({
  showTotalHours,
  data,
  projectCompletion,
  budget,
  loading,
  filter: { afterDateTime, beforeDateTime } = {},
}: ProjectBudgetStatisticsProps) => {
  const { project } = useProject()

  if (data === null) {
    return <CorePageMessage message={'No data found to report on.'} />
  }

  const hoursDuringTimeframe = round(
    data?.subcontractors?.reduce((acc, curr) => acc + curr.projectHours, 0),
    2
  )
  const workersDuringTimeframe = data?.subcontractors?.reduce(
    (acc, curr) => acc + curr.numberOfWorkers,
    0
  )

  return (
    <>
      {afterDateTime && beforeDateTime && (
        <Typography.Text type="secondary">
          Between {moment(afterDateTime ?? project.startDate).format('L')} and{' '}
          {moment(beforeDateTime ?? project.endDate).format('L')}
        </Typography.Text>
      )}
      <Statistics>
        {Boolean(projectCompletion || data?.lastLoggedCompletionPercentage) && (
          <StatWrapper>
            <Statistic
              title="Project completion %"
              value={projectCompletion || data?.lastLoggedCompletionPercentage}
              loading={loading}
            />
          </StatWrapper>
        )}
        {!!data?.completionLastLoggedAt && (
          <StatWrapper>
            <Statistic
              title="Last logged at"
              value={moment(data?.completionLastLoggedAt).format('L')}
              loading={loading}
            />
          </StatWrapper>
        )}
        <StatWrapper>
          <Statistic
            title="Workers"
            value={workersDuringTimeframe}
            loading={loading}
          />
        </StatWrapper>
        <StatWrapper>
          <Statistic
            title="Hours used"
            value={hoursDuringTimeframe}
            loading={loading}
          />
        </StatWrapper>
        <StatWrapper>
          <Statistic
            title={`% hours used`}
            value={`${round((hoursDuringTimeframe / budget) * 100)}%`}
            loading={loading}
          />
        </StatWrapper>
        {showTotalHours && data?.totalHours !== null && (
          <StatWrapper>
            <Statistic
              title={`Total hours as of this date`}
              value={data?.totalHours}
              loading={loading}
            />
          </StatWrapper>
        )}
        <StatWrapper>
          <Statistic
            title={`Status`}
            valueRender={() => (
              <Tag
                variant={
                  data?.status === 'OVER_BUDGET'
                    ? 'danger'
                    : data?.status === 'PROJECTED_OVER_BUDGET'
                      ? 'warning'
                      : 'success'
                }
                label={convertEnumToString(data?.status)}
              />
            )}
            loading={loading}
          />
        </StatWrapper>
      </Statistics>
    </>
  )
}

export default ProjectBudgetStatistics
